import React, {useState, useRef, useCallback} from 'react'
import Popover from '@material-ui/core/Popover'

type Preset = {
  label: string
  value: string
}

type ToolbarPopoverProps = {
  label: string
  placeholder?: string
  defaultValue?: string
  onSubmit: (value: string) => void
  onRemove?: () => void
  children: (props: {onClick: (e: React.MouseEvent) => void}) => React.ReactNode
  multiline?: boolean
  presets?: Preset[]
}

export default function ToolbarPopover({
  label,
  placeholder,
  defaultValue = '',
  onSubmit,
  onRemove,
  children,
  multiline,
  presets,
}: ToolbarPopoverProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [value, setValue] = useState(defaultValue)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  const handleOpen = useCallback(
    (e: React.MouseEvent) => {
      setAnchorEl(e.currentTarget as HTMLElement)
      setValue(defaultValue)
      setTimeout(() => inputRef.current?.focus(), 50)
    },
    [defaultValue],
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSubmit = useCallback(() => {
    if (value) {
      onSubmit(value)
    }
    setAnchorEl(null)
  }, [value, onSubmit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
      if (e.key === 'Escape') {
        handleClose()
      }
    },
    [handleSubmit, handleClose],
  )

  const handlePresetClick = useCallback(
    (presetValue: string) => {
      onSubmit(presetValue)
      setAnchorEl(null)
    },
    [onSubmit],
  )

  const handleRemove = useCallback(() => {
    onRemove?.()
    setAnchorEl(null)
  }, [onRemove])

  const open = Boolean(anchorEl)

  return (
    <>
      {children({onClick: handleOpen})}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableRestoreFocus
      >
        <div className="html-editor-popover">
          <div className="html-editor-popover__label">{label}</div>
          {presets && presets.length > 0 && (
            <div className="html-editor-popover__presets">
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  className="html-editor-popover__preset-btn"
                  type="button"
                  onClick={() => handlePresetClick(preset.value)}
                  title={preset.value}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
          {multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              className="html-editor-popover__textarea"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={4}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              className="html-editor-popover__input"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
            />
          )}
          <div className="html-editor-popover__actions">
            {onRemove && defaultValue && (
              <button
                className="html-editor-popover__btn html-editor-popover__btn--cancel"
                onClick={handleRemove}
                type="button"
                title="Remove"
                style={{marginRight: 'auto'}}
              >
                🗑
              </button>
            )}
            <button
              className="html-editor-popover__btn html-editor-popover__btn--confirm"
              onClick={handleSubmit}
              type="button"
            >
              ✓
            </button>
            <button
              className="html-editor-popover__btn html-editor-popover__btn--cancel"
              onClick={handleClose}
              type="button"
            >
              ✕
            </button>
          </div>
        </div>
      </Popover>
    </>
  )
}
